import { Button, Divider, notification, Tooltip } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../app.logic";
import { callProvince } from "../../../components/address/address.logic";
import {
    createFieldCheckbox,
    createFieldDate,
    createFieldInput,
    createFieldInputNumber,
    createFieldInputPassword,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTreeSelect
} from "../../../components/input/template-builder/template-builder.logic";
import { createColumnImageUrl, createColumnInput } from "../../../components/table/c-table.logic";
import i18n from "../../../i18n";
import store from "../../../store";
import { dateFormat } from "../../../utils/date";
import { download } from "../../../utils/file";
import { conditionFormChecker, putField } from "../../../utils/form";
import { callDocumentTypePerson, callGender, callNationality, callRace, callReligion } from "../../app/company/mgt-tab/tab-4/tab4.section";
import { callTitle } from "../../app/setting/user-mgt/user/user.section";

const Address = defineAsyncComponent(() => import("../../../components/address/Address"));
const AttachmentInlineList = defineAsyncComponent(() => import("../../../components/attachment/AttachmentInlineList"));
const CIcon = defineAsyncComponent(() => import("../../../components/icon/CIcon"));
const CSelect = defineAsyncComponent(() => import("../../../components/input/template-builder/select/CSelect"));
const CDatePicker = defineAsyncComponent(() => import("../../../components/input/template-builder/date-picker/CDatePicker"));
const Consent = defineAsyncComponent(() => import("../../app/pdpa/Consent"));
const Pdpa = defineAsyncComponent(() => import("../../app/pdpa/Pdpa"));

export const callPersonType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "PERSON_TYPE", value: searchQuery });
export const callCompanyType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_TYPE", value: searchQuery });
export const callDepartment = () => store.dispatch("resource/v1/master/department/cacheFindAllWithChildrenType", { type: "main", isRegister: true });
export const callUserOperatorType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_OPERATOR_TYPE", value: searchQuery });
export const callUserObjective = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_OBJECTIVE", value: searchQuery });
export const callRegisterAttachment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "REGISTER_ATTACHMENT", value: searchQuery });
export const callRegisterPersonAttachment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "REGISTER_PERSON_ATTACHMENT", value: searchQuery });
export const callOfficerType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "OFFICER_TYPE", value: searchQuery });
export const callOfficerLv = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "OFFICER_LV", value: searchQuery });
export const callJobPosition = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/jobPosition/cacheFindAll", {
        where: [{ name: "nameTh", type: "String", operator: "like", value: searchQuery }],
        sort: [{ name: "nameTh", type: "ASC" }]
    });
export const callCheckIdTax = ({ idTax }) => store.dispatch("resource/v1/security/user/checkIdTax", { idTax });
export const callCheckIdCard = ({ idCard }) => store.dispatch("resource/v1/security/user/checkIdCard", { idCard });

export const createColumns = () => {
    const columns = [
        createColumnImageUrl({ title: () => i18n.global.t("model.user.refImage"), dataIndex: "refImage.url" }),
        createColumnInput({ title: () => i18n.global.t("model.user.username"), dataIndex: "username" }),
        createColumnInput({ title: () => i18n.global.t("model.user.fullNameTh"), dataIndex: "fullNameTh", width: 200 }),
        createColumnInput({ title: () => i18n.global.t("model.user.fullNameEn"), dataIndex: "fullNameEn", width: 200 }),
        createColumnInput({
            title: () => i18n.global.t("model.user.idCard"),
            dataIndex: "idCard",
            comma: false,
            inputProp: () => ({ comma: false })
        }),
        createColumnInput({ title: () => i18n.global.t("model.user.email"), dataIndex: "email" })
    ];
    return columns;
};

export const createField = ({ props, data, key, isLoading }) => {
    return putField({
        data,
        fields: [
            ...createFieldRegister({ props, data, key, isLoading }),
            ...createFieldUsernamePassword({ props, data, key, isLoading }),
            ...createFieldAcceptCondition({ props, data, key, isLoading })
        ]
    });
};

export const createFieldRegister = ({ props, data, key, isLoading, onLinkIdCard }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            ...createFieldRegister1({ props, data, key, isLoading, onLinkIdCard }),
            ...createFieldRegisterUserInfo({ props, data, key, isLoading }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <Divider></Divider>
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => (
                    <Address
                        menuContext={menuContext}
                        viewOnly={viewOnly}
                        dataProp={data}
                        dataKey={`${prefix}addresses.0`}
                        isLoading={isLoading.value}
                    ></Address>
                )
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <Divider></Divider>
            },
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}buttonCopyAddress`,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => (
                    <Button
                        class="btn btn-primary"
                        onClick={() => {
                            lodash.mergeWith(model.addresses[1], model.addresses[0], (objValue, srcValue, key) => {
                                if (["refId", "refRealId", "refAddressType", "id", "version", "version", "isActive"].includes(key)) return objValue;
                            });
                        }}
                    >
                        {i18n.global.t("views.register.copyAddress")}
                    </Button>
                )
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => (
                    <Address
                        menuContext={menuContext}
                        viewOnly={viewOnly}
                        dataProp={data}
                        dataKey={`${prefix}addresses.1`}
                        isLoading={isLoading.value}
                    ></Address>
                )
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <Divider></Divider>
            },
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}userOperatorTypes`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userOperatorType.modelName"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.userOperatorTypes,
                            "onUpdate:value": value => (model.userOperatorTypes = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callUserOperatorType({ searchQuery }).then(items => items.map(item => ({ refOperatorType: item }))),
                            optLabel: "refOperatorType.value",
                            mode: "multiple",
                            optKey: "refOperatorType.id"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValueChildren: ({ record }) => record.refOperatorType?.value }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}userObjectives`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userObjective.modelName"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.userObjectives,
                            "onUpdate:value": value => (model.userObjectives = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callUserObjective({ searchQuery }).then(items => items.map(item => ({ refObjective: item }))),
                            optLabel: "refObjective.value",
                            mode: "multiple",
                            optKey: "refObjective.id"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValueChildren: ({ record }) => record.refObjective?.value }
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}attachments`,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "AttachmentInlineList" } })
                    );
                    return <AttachmentInlineList {...componentProp}></AttachmentInlineList>;
                }
            }
        ]
    });
};

export const createFieldRegister1 = ({ props, data, key, onLinkIdCard }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refPersonType`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refPersonType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refPersonType,
                            "onUpdate:value": value => {
                                model.refPersonType = value;
                                if (value?.key === "1") {
                                    model.refDocumentTypePerson = null;
                                    useForm.clearValidate(`${prefix}idCard`);
                                }
                            },
                            disabled: viewOnly,
                            list: callPersonType,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}registerDate`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.registerDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        { value: model.registerDate, "onUpdate:value": value => (model.registerDate = value), disabled: viewOnly },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTreeSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refDepartmentRegister`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("model.user.refDepartmentRegister")} (TH)`,
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refDepartmentRegister,
                            "onUpdate:value": value => (model.refDepartmentRegister = value),
                            disabled: viewOnly,
                            list: () => callDepartment().then(data => lodash.cloneDeep(data).filter(d => d.fullCode === '10608').map(d => ({...d, children: []}))),
                            renderLabel: ({ item }) => (
                                <div>
                                    {item.fullCode} : {item.nameTh}
                                </div>
                            ),
                            filterWhere: ({ value }) => [
                                { name: "fullCode", operator: "like", value },
                                { logic: "OR", name: "nameTh", operator: "like", value }
                            ],
                            treeDefaultExpandAll: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refDepartmentRegister.nameEn`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("model.user.refDepartmentRegister")} (EN)`,
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refDepartmentRegister?.nameEn,
                            "onUpdate:value": value => (model.refDepartmentRegister.nameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCompanyType`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refCompanyType"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.refCompanyType");
                    const inputProp = lodash.merge(
                        {
                            value: model.refCompanyType,
                            "onUpdate:value": value => (model.refCompanyType = value),
                            disabled: viewOnly,
                            list: callCompanyType,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (
                        model.refCompanyType &&
                        data.linkIdCard?.companyType?.length >= 0 &&
                        model.refCompanyType.value !== data.linkIdCard.companyType
                    ) {
                        linkCompare = (
                            <>
                                {model.refCompanyType.value}
                                <span className="text-danger">==&gt; {data.linkIdCard.companyType}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CSelect placeholder={placeholder} {...inputProp}></CSelect>
                            {linkCompare}
                        </>
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}companyNameTh`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.companyNameTh"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.companyNameTh");
                    const inputProp = lodash.merge(
                        {
                            value: model.companyNameTh,
                            "onUpdate:value": value => (model.companyNameTh = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.companyNameTh && data.linkIdCard?.companyNameTh?.length >= 0 && model.companyNameTh !== data.linkIdCard.companyNameTh) {
                        linkCompare = (
                            <>
                                {model.companyNameTh}
                                <span className="text-danger">==&gt; {data.linkIdCard.companyNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}companyNameEn`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.companyNameEn"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.companyNameEn");
                    const inputProp = lodash.merge(
                        {
                            value: model.companyNameEn,
                            "onUpdate:value": value => (model.companyNameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.companyNameEn && data.linkIdCard?.companyNameEn?.length >= 0 && model.companyNameEn !== data.linkIdCard.companyNameEn) {
                        linkCompare = (
                            <>
                                {model.companyNameEn}
                                <span className="text-danger">==&gt; {data.linkIdCard.companyNameEn}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refDocumentTypePerson`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refDocumentTypePerson"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refDocumentTypePerson,
                            "onUpdate:value": value => {
                                model.refDocumentTypePerson = value;
                                useForm.validate(`${prefix}idCard`);
                            },
                            disabled: viewOnly,
                            list: callDocumentTypePerson,
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}idCard`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => (["1"].includes(model.refPersonType?.key) ? i18n.global.t("views.register.idTax") : i18n.global.t("model.user.idCard")),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.idCard,
                            "onUpdate:value": value => (model.idCard = value),
                            disabled: viewOnly,
                            comma: false,
                            onBlur: () => useForm.validate(field.key).catch(() => {}),
                            suffix: () => {
                                const forceNoShow = true;
                                if (forceNoShow || !data.linkIdCard?.idCard) {
                                    return null;
                                }
                                let icon = <CIcon class="text-success" file="check.svg"></CIcon>;
                                if (data.linkIdCard?.idCard !== model.idCard) {
                                    icon = <CIcon class="text-danger" file="close.svg"></CIcon>;
                                }
                                return <Tooltip title={data.linkIdCard?.idCard}>{icon}</Tooltip>;
                            }
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}buttonCheckIdCard`,
                colProp: () => ({ md: 4 }),
                formItemRender: () => {
                    let manual = <div></div>;
                    if (["2"].includes(model.refPersonType?.key)) {
                        manual = (
                            <a
                                class="text-center"
                                href="javascript:void(0)"
                                onClick={() =>
                                    download({
                                        file: { name: i18n.global.t("views.register.linkageClient"), url: "/files/server/LinkageClient.exe" }
                                    })
                                }
                            >
                                ({i18n.global.t("views.register.downloadLinkageClient")})
                            </a>
                        );
                    }
                    return (
                        <>
                            <div>
                                <Button
                                    class="mt-auto mb-auto btn-primary"
                                    onClick={() => {
                                        setIsLoadingApp(true);
                                        if (["1"].includes(model.refPersonType?.key)) {
                                            callCheckIdTax({ idTax: model.idCard })
                                                .then(res => {
                                                    //console.log(res);
                                                    if (!res) return null;
                                                    model.isVerify = true;
                                                    const resultList = res?.ResultList?.[0];
                                                    return {
                                                        idCard: resultList.JuristicID,
                                                        companyNameTh: resultList.JuristicName_TH,
                                                        companyNameEn: resultList.JuristicName_EN,
                                                        companyType: resultList.JuristicType,
                                                        addresses: resultList.AddressInformations.map(d => ({
                                                            name: d.AddressName,
                                                            addrTh: d.FullAddress,
                                                            province: d.Province,
                                                            amphur: d.Ampur,
                                                            tumbol: d.Tumbol,
                                                            tel: d.Phone
                                                        }))
                                                    };
                                                })
                                                .then(d => onLinkIdCard?.((data.linkIdCard = d)))
                                                .finally(() => setIsLoadingApp(false));
                                        } else if (["2"].includes(model.refPersonType?.key)) {
                                            callCheckIdCard({ idCard: model.idCard }).finally(() => {
                                                setTimeout(() => {
                                                    setIsLoadingApp(false);
                                                    if (!data.linkageReceive001 || !data.linkageReceive009) {
                                                        notification["error"]({ message: i18n.global.t("error.linkage.noResponse") });
                                                    }
                                                }, 10000);
                                            });
                                        }
                                    }}
                                >
                                    {["1"].includes(model.refPersonType?.key)
                                        ? i18n.global.t("views.register.buttonCheckIdTax")
                                        : i18n.global.t("views.register.buttonCheckIdCard")}
                                </Button>
                            </div>
                            {manual}
                        </>
                    );
                }
            },
            {
                visible: () => data.linkIdCard?.addresses?.length > 0,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.register.linkIdCardAddress"),
                inputRender: () => {
                    return (
                        <>
                            {data.linkIdCard?.addresses?.map((d, index) => (
                                <div class="text-danger">
                                    {data.linkIdCard?.addresses?.length > 1 ? index + 1 + "." : ""} {d.name} {d.addrTh} {d.tumbol} {d.amphur}{" "}
                                    {d.province} {d.tel}
                                </div>
                            ))}
                        </>
                    );
                }
            },
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refIssuedBy`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refIssuedBy"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refIssuedBy,
                            "onUpdate:value": value => (model.refIssuedBy = value),
                            disabled: viewOnly,
                            list: callProvince,
                            optLabel: "nameTh"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}outDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.outDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        { value: model.outDate, "onUpdate:value": value => (model.outDate = value), disabled: viewOnly },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}expireDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.expireDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.expireDate,
                            "onUpdate:value": value => (model.expireDate = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}birthDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.birthDate"),
                inputRender: ({ field }) => {
                    const inputProp = lodash.merge(
                        { value: model.birthDate, "onUpdate:value": value => (model.birthDate = value), disabled: viewOnly },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.birthDate && data.linkIdCard?.birthDate?.length >= 0 && model.birthDate !== data.linkIdCard.birthDate) {
                        linkCompare = (
                            <>
                                {dateFormat({ text: model.birthDate, format: "DD/MM/YYYY" })}
                                <span className="text-danger">==&gt; {dateFormat({ text: data.linkIdCard.birthDate, format: "DD/MM/YYYY" })}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CDatePicker {...inputProp}></CDatePicker>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refNationality`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refNationality"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.refNationality");
                    const inputProp = lodash.merge(
                        {
                            value: model.refNationality,
                            "onUpdate:value": value => (model.refNationality = value),
                            disabled: viewOnly,
                            list: callNationality,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (
                        model.refNationality &&
                        data.linkIdCard?.nationalityDesc?.length >= 0 &&
                        model.refNationality.value !== data.linkIdCard.nationalityDesc
                    ) {
                        linkCompare = (
                            <>
                                {model.refNationality.value}
                                <span className="text-danger">==&gt; {data.linkIdCard.nationalityDesc}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CSelect placeholder={placeholder} {...inputProp}></CSelect>
                            {linkCompare}
                        </>
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refRace`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refRace"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refRace,
                            "onUpdate:value": value => (model.refRace = value),
                            disabled: viewOnly,
                            list: callRace,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refReligion`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refReligion"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refReligion,
                            "onUpdate:value": value => (model.refReligion = value),
                            disabled: viewOnly,
                            list: callReligion,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refGender`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refGender"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.refGender");
                    const inputProp = lodash.merge(
                        {
                            value: model.refGender,
                            "onUpdate:value": value => (model.refGender = value),
                            disabled: viewOnly,
                            list: callGender,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.refGender && data.linkIdCard?.genderDesc?.length >= 0 && model.refGender.value !== data.linkIdCard.genderDesc) {
                        linkCompare = (
                            <>
                                {model.refGender.value}
                                <span className="text-danger">==&gt; {data.linkIdCard.genderDesc}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CSelect placeholder={placeholder} {...inputProp}></CSelect>
                            {linkCompare}
                        </>
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            })
        ]
    });
};

export const createFieldRegisterUserInfo = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refTitle`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("model.user.refTitle")} (TH)`,
                inputRender: ({ field }) => {
                    const placeholder = `${i18n.global.t("model.user.refTitle")} (TH)`;
                    const inputProp = lodash.merge(
                        {
                            value: model.refTitle,
                            "onUpdate:value": value => (model.refTitle = value),
                            disabled: viewOnly,
                            list: callTitle,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.refTitle && data.linkIdCard?.titleName?.length >= 0 && model.refTitle.value !== data.linkIdCard.titleName) {
                        linkCompare = (
                            <>
                                {model.refTitle.value}
                                <span className="text-danger">==&gt; {data.linkIdCard.titleName}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CSelect placeholder={placeholder} {...inputProp}></CSelect>
                            {linkCompare}
                        </>
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refTitle`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("model.user.refTitle")} (EN)`,
                inputProp: () => ({
                    value: model.refTitle,
                    "onUpdate:value": value => (model.refTitle = value),
                    disabled: true,
                    list: callTitle,
                    optLabel: "value2"
                }),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}firstNameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.firstNameTh"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.firstNameTh");
                    const inputProp = lodash.merge(
                        {
                            value: model.firstNameTh,
                            "onUpdate:value": value => (model.firstNameTh = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.firstNameTh && data.linkIdCard?.firstNameTh?.length >= 0 && model.firstNameTh !== data.linkIdCard.firstNameTh) {
                        linkCompare = (
                            <>
                                {model.firstNameTh}
                                <span className="text-danger">==&gt; {data.linkIdCard.firstNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}firstNameEn`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.firstNameEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.firstNameEn,
                            "onUpdate:value": value => (model.firstNameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}middleNameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.middleNameTh"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.middleNameTh");
                    const inputProp = lodash.merge(
                        {
                            value: model.middleNameTh,
                            "onUpdate:value": value => (model.middleNameTh = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.middleNameTh && data.linkIdCard?.middleNameTh?.length >= 0 && model.middleNameTh !== data.linkIdCard.middleNameTh) {
                        linkCompare = (
                            <>
                                {model.middleNameTh}
                                <span className="text-danger">==&gt; {data.linkIdCard.middleNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}middleNameEn`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.middleNameEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.middleNameEn,
                            "onUpdate:value": value => (model.middleNameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastNameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.lastNameTh"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.lastNameTh");
                    const inputProp = lodash.merge(
                        {
                            value: model.lastNameTh,
                            "onUpdate:value": value => (model.lastNameTh = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.lastNameTh && data.linkIdCard?.lastNameTh?.length >= 0 && model.lastNameTh !== data.linkIdCard.lastNameTh) {
                        linkCompare = (
                            <>
                                {model.lastNameTh}
                                <span className="text-danger">==&gt; {data.linkIdCard.lastNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastNameEn`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.lastNameEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastNameEn,
                            "onUpdate:value": value => (model.lastNameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}mobile`,
                colProp: () => model?.refPersonType?.key === "1" ? ({ xs: 24, md: 20 }) : ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.mobile"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.mobile,
                            "onUpdate:value": value => (model.mobile = value),
                            disabled: viewOnly,
                            comma: false,
                            maxDecimal: 10
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldRegisterOfficerInfo = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTreeSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refDepartment`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("views.register.refDepartment")} (TH)`,
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refDepartment,
                            "onUpdate:value": value => (model.refDepartment = value),
                            disabled: viewOnly,
                            list: callDepartment,
                            renderLabel: ({ item }) => (
                                <div>
                                    {item.fullCode ? item.fullCode + " : " : ''}{item.nameTh}
                                </div>
                            ),
                            filterWhere: ({ value }) => [
                                { name: "fullCode", operator: "like", value },
                                { logic: "OR", name: "nameTh", operator: "like", value }
                            ],
                            treeDefaultExpandAll: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refDepartment.nameEn`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("views.register.refDepartment")} (EN)`,
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refDepartment?.nameEn,
                            "onUpdate:value": value => (model.refDepartment.nameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refJobPosition`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("model.user.refJobPosition")} (TH)`,
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refJobPosition,
                            "onUpdate:value": value => (model.refJobPosition = value),
                            disabled: viewOnly,
                            list: callJobPosition,
                            optLabel: "nameTh"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refJobPosition.nameEn`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => `${i18n.global.t("model.user.refJobPosition")} (EN)`,
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refJobPosition?.nameEn,
                            "onUpdate:value": value => (model.refJobPosition.nameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refOfficerLv`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refOfficerLv"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refOfficerLv,
                            "onUpdate:value": value => (model.refOfficerLv = value),
                            disabled: viewOnly,
                            list: callOfficerLv,
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refOfficerType`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refOfficerType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refOfficerType,
                            "onUpdate:value": value => (model.refOfficerType = value),
                            disabled: viewOnly,
                            list: callOfficerType,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
        ]
    });
};

export const createFieldUsernamePassword = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}username`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => "1".includes(model.refPersonType?.key) ? i18n.global.t("model.user.usernameJuristic") : i18n.global.t("model.user.username"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.username,
                            "onUpdate:value": value => (model.username = value),
                            disabled: viewOnly,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputPassword({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}password`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.password"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.password,
                            "onUpdate:value": value => (model.password = value),
                            disabled: viewOnly,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputPassword({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}passwordConfirm`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.passwordConfirm"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.passwordConfirm,
                            "onUpdate:value": value => (model.passwordConfirm = value),
                            disabled: viewOnly,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldAcceptCondition = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <Divider></Divider>
            },
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}acceptCondition`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 1 }, wrapperCol: { xs: 23 }, colon: false }),
                label: () => " ",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            checked: model.acceptCondition,
                            "onUpdate:checked": checked => (model.acceptCondition = checked),
                            disabled: viewOnly,
                            text: () => <span>
                                {i18n.global.t("views.register.acceptCondition")}
                                <a href="javascript:void(0)"
                                   onClick={ () => addModal({
                                       bind: {
                                           okButtonProps: { style: {"display": "none"} },
                                           title: i18n.global.t("views.register.acceptConditionLink"),
                                           width: "95%"
                                       },
                                       render: () => (<Consent></Consent>)
                                   })}
                                > <u>{i18n.global.t("views.register.acceptConditionLink")}</u></a>
                            </span>
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}isAcceptPdpa`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 1 }, wrapperCol: { xs: 23 }, colon: false }),
                label: () => " ",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            checked: model.isAcceptPdpa,
                            "onUpdate:checked": checked => (model.isAcceptPdpa = checked),
                            disabled: viewOnly,
                            text: () =>(<>
                                <span>{i18n.global.t("views.register.isAcceptPdpa")}</span>
                                <span>{i18n.global.t("views.register.isAcceptPdpa2")}
                                    <a href="javascript:void(0)"
                                       onClick={ () => addModal({
                                           bind: {
                                               okButtonProps: { style: {"display": "none"} },
                                               title: i18n.global.t("views.register.acceptConditionLink"),
                                               width: "95%"
                                           },
                                           render: () => (<Pdpa></Pdpa>)
                                       })}
                                    > <u>{i18n.global.t("views.register.pdpaTitle")}</u></a>
                                </span> {i18n.global.t("views.register.isAcceptPdpa3")}
                            </>)
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};
